import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Grid,
  CircularProgress,
  InputAdornment,
  Chip,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Card,
  CardContent,
  Avatar,
  Badge,
  Snackbar
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  AttachFile as AttachmentIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
  AutoFixHigh as AutoFixHighIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Category as CategoryIcon,
  LocalOffer as PriceIcon,
  Inventory as StockIcon,
  Info as InfoIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Assignment as AttributeIcon
} from '@mui/icons-material';
import { useApiService } from '../../hooks/useApiService';
import pell from 'pell';
import 'pell/dist/pell.min.css';
import { useNavigate } from 'react-router-dom';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.12)',
  marginBottom: theme.spacing(3),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  borderRadius: theme.spacing(2),
  minWidth: 150,
}));

const FloatingButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

const DescriptionContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
}));

const StyledTextArea = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-root': {
    fontFamily: theme.typography.fontFamily,
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  '& .MuiInputBase-multiline': {
    padding: theme.spacing(2),
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
  },
}));

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.7;
  }
`;

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 2,
  borderRadius: theme.shape.borderRadius,
}));

const AnimatedCircle = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  animation: `${pulse} 1.5s infinite ease-in-out`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.primary.contrastText,
}));

const EditorWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  '.pell': {
    borderRadius: theme.spacing(1),
  },
  '.pell-content': {
    minHeight: '250px',
    backgroundColor: '#ffffff',
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  '.pell-actionbar': {
    backgroundColor: '#f8f9fa',
    border: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '.pell-button': {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    height: '30px',
    width: '30px',
    margin: '0 2px',
    borderRadius: '3px',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '.pell-button-selected': {
    backgroundColor: theme.palette.action.selected,
  }
}));

// পণ্যের ক্যাটেগরি - Bangla Categories
const categories = [
  { name: "সবজি", icon: "🥬", subCategories: ["পাতা জাতীয়", "মূল জাতীয়", "ফল জাতীয়"] },
  { name: "ফল", icon: "🍎", subCategories: ["দেশি ফল", "বিদেশি ফল", "মৌসুমি ফল"] },
  { name: "চাল ও ডাল", icon: "🌾", subCategories: ["চাল", "ডাল", "খই", "চিড়া"] },
  { name: "মাংস ও মাছ", icon: "🍖", subCategories: ["গরুর মাংস", "খাসির মাংস", "মুরগির মাংস", "দেশি মাছ", "সামুদ্রিক মাছ"] },
  { name: "দুগ্ধজাত", icon: "🥛", subCategories: ["দুধ", "দই", "পনির", "মাখন", "ঘি"] },
  { name: "মশলা", icon: "🌶️", subCategories: ["গরম মশলা", "ভেষজ মশলা", "তেল ও ভিনেগার"] },
  { name: "শুকনো খাবার", icon: "🥜", subCategories: ["বাদাম", "কিশমিশ", "খেজুর", "শুকনো ফল"] },
  { name: "পানীয়", icon: "🥤", subCategories: ["চা", "কফি", "জুস", "পানি"] },
  { name: "মিষ্টি ও স্ন্যাকস", icon: "🍬", subCategories: ["মিষ্টি", "বিস্কুট", "চিপস", "নুডলস"] },
  { name: "গৃহস্থালী সামগ্রী", icon: "🧴", subCategories: ["পরিষ্কারের সামগ্রী", "স্বাস্থ্য সামগ্রী", "রান্নার সামগ্রী"] }
];

// পরিমাপের একক - Bangla Units
const units = [
  { value: "কেজি", label: "কেজি (Kg)" },
  { value: "গ্রাম", label: "গ্রাম (Gram)" },
  { value: "পিস", label: "পিস (Piece)" },
  { value: "ডজন", label: "ডজন (Dozen)" },
  { value: "লিটার", label: "লিটার (Liter)" },
  { value: "মিলি", label: "মিলিলিটার (ml)" },
  { value: "প্যাক", label: "প্যাক (Pack)" },
  { value: "বান্ডিল", label: "বান্ডিল (Bundle)" },
  { value: "হালি", label: "হালি (Handful)" }
];

// পণ্যের বৈশিষ্ট্য - Product Attributes
const productAttributes = [
  {
    name: "গুণমান",
    type: "select",
    options: ["প্রিমিয়াম", "স্ট্যান্ডার্ড", "বাজেট"]
  },
  {
    name: "উৎপাদনের ধরন",
    type: "select", 
    options: ["জৈব", "প্রাকৃতিক", "হাইব্রিড", "প্রচলিত"]
  },
  {
    name: "সংরক্ষণ পদ্ধতি",
    type: "select",
    options: ["ফ্রিজে রাখুন", "ঠান্ডা জায়গায়", "সাধারণ তাপমাত্রায়", "শুকনো জায়গায়"]
  },
  {
    name: "বিশেষ বৈশিষ্ট্য",
    type: "checkbox",
    options: ["হালাল", "ভেগান", "গ্লুটেন ফ্রি", "কৃত্রিম রং মুক্ত", "প্রিজারভেটিভ মুক্ত"]
  },
  {
    name: "স্বাদ",
    type: "select",
    options: ["মিষ্টি", "টক", "ঝাল", "তেতো", "লবণাক্ত", "নরম", "মিশ্র"]
  }
];

const AddProduct = () => {
  // মূল পণ্যের তথ্য
  const [productName, setProductName] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("");
  const [stockQuantity, setStockQuantity] = useState("");
  const [minimumOrder, setMinimumOrder] = useState("");
  const [brand, setBrand] = useState("");
  const [origin, setOrigin] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  
  // বৈশিষ্ট্য সংক্রান্ত
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [customAttributes, setCustomAttributes] = useState([]);
  const [newAttributeName, setNewAttributeName] = useState("");
  const [newAttributeValue, setNewAttributeValue] = useState("");
  
  // UI অবস্থা
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [language, setLanguage] = useState("বাংলা");
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // রেফারেন্স
  const editorRef = useRef(null);
  const navigate = useNavigate();
  const { apiService } = useApiService();

  useEffect(() => {
    // Initialize Pell editor
    if (editorRef.current && !editorRef.current._pell) {
      try {
        const editor = pell.init({
          element: editorRef.current,
          onChange: handleDescriptionChange,
          defaultParagraphSeparator: 'div',
          styleWithCSS: true,
          actions: [
            'bold', 'italic', 'underline',
            'strikethrough', 'heading1', 'heading2',
            'paragraph', 'quote', 'olist', 'ulist',
            'code', 'line', 'link', 'image'
          ],
          classes: {
            actionbar: 'pell-actionbar',
            button: 'pell-button',
            content: 'pell-content',
            selected: 'pell-button-selected'
          }
        });
        
        editorRef.current._pell = editor;
      } catch (error) {
        console.error('Error initializing Pell editor:', error);
      }
    }

    return () => {
      if (editorRef.current && editorRef.current._pell) {
        try {
          editorRef.current.innerHTML = '';
          editorRef.current._pell = null;
        } catch (error) {
          console.error('Error cleaning up Pell editor:', error);
        }
      }
    };
  }, []);

  const handleDescriptionChange = (html) => {
    setDescription(html);
  };

  const handleCategoryChange = (selectedCategory) => {
    setCategory(selectedCategory);
    setSubCategory(""); // Reset sub-category when main category changes
  };

  const handleAttributeChange = (attributeName, value, isCheckbox = false) => {
    setSelectedAttributes(prev => {
      if (isCheckbox) {
        const currentValues = prev[attributeName] || [];
        const updatedValues = currentValues.includes(value)
          ? currentValues.filter(v => v !== value)
          : [...currentValues, value];
        return { ...prev, [attributeName]: updatedValues };
      } else {
        return { ...prev, [attributeName]: value };
      }
    });
  };

  const addCustomAttribute = () => {
    if (newAttributeName && newAttributeValue) {
      setCustomAttributes(prev => [
        ...prev,
        { name: newAttributeName, value: newAttributeValue }
      ]);
      setNewAttributeName("");
      setNewAttributeValue("");
    }
  };

  const removeCustomAttribute = (index) => {
    setCustomAttributes(prev => prev.filter((_, i) => i !== index));
  };

  const handleClear = () => {
    setProductName("");
    setCategory("");
    setSubCategory("");
    setDescription("");
    setPrice("");
    setUnit("");
    setStockQuantity("");
    setMinimumOrder("");
    setBrand("");
    setOrigin("");
    setExpiryDate("");
    setSelectedAttributes({});
    setCustomAttributes([]);
    setErrorMessage("");
    setSuccessMessage("");
    
    if (editorRef.current && editorRef.current._pell) {
      editorRef.current._pell.content.innerHTML = '';
    }
  };

  const handleSave = () => {
    // Validation
    if (!productName || !category || !price || !unit) {
      setSnackbar({
        open: true,
        message: "অনুগ্রহ করে সকল আবশ্যক ক্ষেত্র পূরণ করুন",
        severity: "error"
      });
      return;
    }

    const productData = {
      name: productName,
      category,
      subCategory,
      description,
      price: parseFloat(price),
      unit,
      stockQuantity: parseInt(stockQuantity) || 0,
      minimumOrder: parseInt(minimumOrder) || 1,
      brand,
      origin,
      expiryDate,
      attributes: selectedAttributes,
      customAttributes
    };

    console.log("পণ্যের তথ্য সংরক্ষণ করা হচ্ছে:", productData);
    
    setSnackbar({
      open: true,
      message: "পণ্য সফলভাবে যোগ করা হয়েছে!",
      severity: "success"
    });

    // Reset form after successful save
    setTimeout(() => {
      handleClear();
    }, 2000);
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const selectedCategoryData = categories.find(cat => cat.name === category);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* পেজ হেডার */}
      <StyledPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
            <AddIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
              নতুন পণ্য যোগ করুন
            </Typography>
            <Typography variant="body1" color="text.secondary">
              আপনার দোকানে নতুন পণ্য যোগ করুন এবং বিক্রয় শুরু করুন
            </Typography>
          </Box>
        </Box>
      </StyledPaper>

      {/* মূল তথ্য */}
      <StyledPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <InfoIcon sx={{ color: 'primary.main', mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            পণ্যের মূল তথ্য
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                ক্যাটেগরি নির্বাচন
              </Typography>
              <Typography variant="body2" color="text.secondary">
                প্রথমে প্রধান ক্যাটেগরি নির্বাচন করুন, তারপর উপযুক্ত উপ-ক্যাটেগরি বেছে নিন
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="পণ্যের নাম *"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              variant="outlined"
              placeholder="যেমন: টাজা টমেটো"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="ব্র্যান্ড"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              variant="outlined"
              placeholder="যেমন: ফ্রেশ ফার্মস"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>প্রধান ক্যাটেগরি *</InputLabel>
              <Select
                value={category}
                onChange={(e) => handleCategoryChange(e.target.value)}
                label="প্রধান ক্যাটেগরি *"
                MenuProps={{
                  disableScrollLock: true,
                }}
              >
                <MenuItem value="">
                  <em>প্রধান ক্যাটেগরি নির্বাচন করুন</em>
                </MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.name} value={cat.name}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: 8 }}>{cat.icon}</span>
                      {cat.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!category}>
              <InputLabel>উপ-ক্যাটেগরি</InputLabel>
              <Select
                value={subCategory}
                onChange={(e) => setSubCategory(e.target.value)}
                label="উপ-ক্যাটেগরি"
                MenuProps={{
                  disableScrollLock: true,
                }}
              >
                <MenuItem value="">
                  <em>
                    {category 
                      ? "উপ-ক্যাটেগরি নির্বাচন করুন" 
                      : "প্রথমে প্রধান ক্যাটেগরি নির্বাচন করুন"
                    }
                  </em>
                </MenuItem>
                {selectedCategoryData?.subCategories.map((subCat) => (
                  <MenuItem key={subCat} value={subCat}>
                    {subCat}
                  </MenuItem>
                )) || []}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="উৎপাদনের স্থান"
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              variant="outlined"
              placeholder="যেমন: বগুড়া, বাংলাদেশ"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="দাম (টাকা) *"
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              variant="outlined"
              InputProps={{
                startAdornment: <InputAdornment position="start">৳</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>পরিমাপের একক *</InputLabel>
              <Select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                label="পরিমাপের একক *"
                MenuProps={{
                  disableScrollLock: true,
                }}
              >
                {units.map((u) => (
                  <MenuItem key={u.value} value={u.value}>
                    {u.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="স্টক পরিমাণ"
              type="number"
              value={stockQuantity}
              onChange={(e) => setStockQuantity(e.target.value)}
              variant="outlined"
              placeholder="100"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="সর্বনিম্ন অর্ডার পরিমাণ"
              type="number"
              value={minimumOrder}
              onChange={(e) => setMinimumOrder(e.target.value)}
              variant="outlined"
              placeholder="1"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="মেয়াদ উত্তীর্ণের তারিখ"
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>
      </StyledPaper>

      {/* পণ্যের বিবরণ */}
      <StyledPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <DescriptionIcon sx={{ color: 'primary.main', mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            পণ্যের বিবরণ
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        <DescriptionContainer>
          <EditorWrapper>
            <div 
              ref={editorRef} 
              className="pell-editor-container" 
              style={{ pointerEvents: isTyping || loading ? 'none' : 'auto' }}
            />
            
            {loading && (
              <LoadingOverlay>
                <AnimatedCircle>
                  <CircularProgress color="inherit" size={30} />
                </AnimatedCircle>
              </LoadingOverlay>
            )}
            
            <Tooltip title="স্বয়ংক্রিয় বিবরণ তৈরি করুন">
              <FloatingButton
                color="primary"
                onClick={() => setOpenDialog(true)}
                disabled={loading || !productName || isTyping}
                size="small"
                sx={{ top: '45px', zIndex: 3 }}
              >
                <AutoFixHighIcon />
              </FloatingButton>
            </Tooltip>
          </EditorWrapper>
        </DescriptionContainer>
      </StyledPaper>

      {/* পণ্যের বৈশিষ্ট্য */}
      <StyledPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AttributeIcon sx={{ color: 'primary.main', mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            পণ্যের বৈশিষ্ট্য
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {productAttributes.map((attribute) => (
          <Accordion key={attribute.name} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">{attribute.name}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {attribute.type === "select" ? (
                <FormControl fullWidth>
                  <InputLabel>{attribute.name} নির্বাচন করুন</InputLabel>
                  <Select
                    value={selectedAttributes[attribute.name] || ""}
                    onChange={(e) => handleAttributeChange(attribute.name, e.target.value)}
                    label={`${attribute.name} নির্বাচন করুন`}
                    MenuProps={{
                      disableScrollLock: true,
                    }}
                  >
                    {attribute.options.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Box>
                  {attribute.options.map((option) => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Checkbox
                          checked={(selectedAttributes[attribute.name] || []).includes(option)}
                          onChange={(e) => handleAttributeChange(attribute.name, option, true)}
                        />
                      }
                      label={option}
                    />
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}

        {/* কাস্টম বৈশিষ্ট্য যোগ করুন */}
        <Card sx={{ mt: 3, bgcolor: '#f8f9fa' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              কাস্টম বৈশিষ্ট্য যোগ করুন
            </Typography>
            
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="বৈশিষ্ট্যের নাম"
                  value={newAttributeName}
                  onChange={(e) => setNewAttributeName(e.target.value)}
                  placeholder="যেমন: রং"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="বৈশিষ্ট্যের মান"
                  value={newAttributeValue}
                  onChange={(e) => setNewAttributeValue(e.target.value)}
                  placeholder="যেমন: লাল"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addCustomAttribute}
                  disabled={!newAttributeName || !newAttributeValue}
                  fullWidth
                >
                  যোগ করুন
                </Button>
              </Grid>
            </Grid>

            {customAttributes.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  যোগকৃত কাস্টম বৈশিষ্ট্য:
                </Typography>
                <List>
                  {customAttributes.map((attr, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={attr.name}
                        secondary={attr.value}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => removeCustomAttribute(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </CardContent>
        </Card>
      </StyledPaper>

      {/* পণ্যের ছবি */}
      <StyledPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <ImageIcon sx={{ color: 'primary.main', mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            পণ্যের ছবি
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<AddPhotoAlternateIcon />}
            component="label"
            size="large"
          >
            ছবি আপলোড করুন
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
            />
          </Button>
          <Typography variant="body2" color="text.secondary">
            সর্বোচ্চ ৫টি ছবি আপলোড করুন। প্রতিটি ছবির সাইজ সর্বোচ্চ ২ মেগাবাইট।
          </Typography>
        </Box>
      </StyledPaper>

      {/* অ্যাকশন বাটন */}
      <StyledPaper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              * চিহ্নিত ক্ষেত্রগুলো আবশ্যক
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClear}
              size="large"
            >
              পরিষ্কার করুন
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!productName || !category || !price || !unit}
              size="large"
            >
              পণ্য সংরক্ষণ করুন
            </Button>
          </Box>
        </Box>
      </StyledPaper>

      {/* ডায়ালগ */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>বিবরণ তৈরির ভাষা নির্বাচন করুন</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>ভাষা</InputLabel>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              label="ভাষা"
              MenuProps={{
                disableScrollLock: true,
              }}
            >
              <MenuItem value="বাংলা">বাংলা</MenuItem>
              <MenuItem value="English">English</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>বাতিল</Button>
          <Button 
            onClick={() => {
              setOpenDialog(false);
              // এখানে AI দিয়ে বিবরণ তৈরির কোড যুক্ত করা যাবে
            }} 
            variant="contained"
            disabled={loading}
          >
            বিবরণ তৈরি করুন
          </Button>
        </DialogActions>
      </Dialog>

      {/* স্ন্যাকবার */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddProduct;
